import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import UploadIcon from '../../assets/icons/upload-icon.inline.svg';
import PictureIcon from '../../assets/icons/picture-icon.inline.svg';
import InputGroupWrapper from './InputGroup';
import translatable from '../../utils/propTypes/translatable';
import { uploadFile } from '../../services/resources/dossierItems';
import Loading from '../Loading';
import { useHandleError } from '../../services/api';
import useAuthenticate from '../../hooks/useAuthenticate';
import { useFormikContext } from 'formik';

const messages = defineMessages({
  dropzonePlaceholder: {
    id: 'cc4.form.upload-documents.file.placeholder',
    defaultMessage: '<primary>Browse file</primary> or drag here',
  },
  takeAPhoto: {
    id: 'cc4.form.upload-documents.take-a-photo',
    defaultMessage: 'Take a photo',
  },
  uploadFile: {
    id: 'cc4.form.upload-documents.upload-file',
    defaultMessage: 'Upload a file',
  },
  orSeparatorText: {
    id: 'cc4.form.upload-docs.or-separator-text',
    defaultMessage: 'or',
  },
  pleaseSelectCategoryInfo: {
    id: 'cc4.form.upload-other-document.please-select-category-info',
    defaultMessage: 'Please select category',
  },
});

const Placeholder = ({ fileIsUploading, formatMessage, categoryId }) => (
  <>
    <div className="sm:hidden flex h-full py-15 justify-center items-center">
      <div
        className="mobile-dossier-placeholder separator border-r border-gray-200"
        data-separator-text={formatMessage(messages.orSeparatorText)}
      >
        <PictureIcon className="fill-primary" />
        <p>{formatMessage(messages.takeAPhoto)}</p>
      </div>
      <div className="mobile-dossier-placeholder">
        <UploadIcon className="fill-primary" />
        <p>{formatMessage(messages.uploadFile)}</p>
      </div>
    </div>
    <div className="hidden sm:flex h-full justify-center items-center p-20">
      {fileIsUploading ? (
        <Loading />
      ) : (
        <>
          <UploadIcon className="fill-primary" />
          <p className="ml-15 mt-0">
            {categoryId
              ? formatMessage(messages.dropzonePlaceholder, {
                  // eslint-disable-next-line
                  primary: (...chunks) => (
                    <span className="text-primary-400">{chunks}</span>
                  ),
                })
              : formatMessage(messages.pleaseSelectCategoryInfo)}
          </p>
        </>
      )}
    </div>
  </>
);

Placeholder.propTypes = {
  categoryId: PropTypes.string,
  fileIsUploading: PropTypes.bool.isRequired,
  formatMessage: PropTypes.func.isRequired,
};

// eslint-disable-next-line react/no-multi-comp
const DropzoneWrapper = ({
  name,
  relationship,
  afterSuccess,
  afterUploadCallback,
  // tslint:disable-next-line:no-shadowed-variable
  placeholder: Placeholder,
  arrayHelpers,
  values,
  setProgressPercentage,
  categoryId,
}) => {
  const { formatMessage } = useIntl();

  const { token } = useAuthenticate();

  const { setFieldValue, setFieldTouched } = useFormikContext();

  const [handleError] = useHandleError();
  const [fileIsUploading, setFileIsUploading] = useState(false);

  function onDrop(acceptedFiles) {
    setFieldValue(name, [...values, ...acceptedFiles]);

    if (acceptedFiles.length) {
      acceptedFiles.forEach(async (file, index) => {
        const config = {
          onUploadProgress: progressEvent => {
            const percentageCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );

            setProgressPercentage &&
              setProgressPercentage(prevState => ({
                ...prevState,
                [values.length + index]: percentageCompleted,
              }));
          },
        };
        try {
          setFileIsUploading(true);
          const { fileId, dossierItemId } = await uploadFile({
            file,
            relationship,
            config: {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              ...config,
            },
          }).catch(error => {
            setFileIsUploading(false);
            handleError(error);
          });
          setFieldTouched(name);

          let newFile = Object.assign(file, {
            preview: URL.createObjectURL(file),
            fileId,
            dossierItemId,
          });

          arrayHelpers.replace(values.length + index, newFile);

          afterUploadCallback && afterUploadCallback(fileId, dossierItemId);
          afterSuccess && (await afterSuccess());
          setFileIsUploading(false);
        } catch (error) {
          handleError(error);
        }
      });
    }
  }

  return (
    <Dropzone onDrop={onDrop} disabled={!categoryId}>
      {({ getRootProps, getInputProps }) => (
        <div
          className="
            w-full h-150 relative
            border-primary-400 border border-dashed rounded
            font-pt-sans text-root cursor-pointer"
          {...getRootProps()}
        >
          <input
            data-testid="dropzone"
            {...getInputProps()}
            accept=".doc,.docx,.xls,.xlsx,.pdf,.bmp,.jpg,.jpeg,.png,.gif,.rtf,.txt,.pdf"
          />
          <Placeholder
            fileIsUploading={fileIsUploading}
            formatMessage={formatMessage}
            categoryId={categoryId}
          />
        </div>
      )}
    </Dropzone>
  );
};

DropzoneWrapper.defaultProps = {
  placeholder: Placeholder,
};

DropzoneWrapper.propTypes = {
  afterSuccess: PropTypes.func,
  afterUploadCallback: PropTypes.func,
  arrayHelpers: PropTypes.object,
  categoryId: PropTypes.string,
  field: PropTypes.object,
  form: PropTypes.object,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.func,
  relationship: PropTypes.object,
  setProgressPercentage: PropTypes.func,
  token: PropTypes.string,
  values: PropTypes.array,
};

// eslint-disable-next-line
const File = props => (
  <InputGroupWrapper
    headerLabel
    {...props}
    className="mb-20 mt-10 last:mb-0 sm:inline-block w-full sm:max-w-none"
    component={DropzoneWrapper}
  />
);

File.propTypes = {
  label: translatable,
  name: PropTypes.string.isRequired,
  placeholder: translatable,
};

export { DropzoneWrapper };

export default File;
